// src/features/activity/logic.ts
// Pure helpers shared across the activity feature — no React imports.

import type { Entity } from '@core/types/entity';
import type { ActivityCategory, ActivityScope } from '@core/types/activity';

export interface ScopeIds {
  platformId: string;
  productId: string;
  tenantId: string;
}

/** Build a fully-populated Entity reference from an id and the current scope. */
export function makeEntity(id: string, typeName: string, scope: ScopeIds): Entity {
  return {
    type_id: '',
    type_name: typeName,
    id,
    platform_id: scope.platformId,
    tenant_id: scope.tenantId,
    product_id: scope.productId,
  };
}

export function errMessage(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

export function fmtDateTime(value: string | undefined, locale = 'en'): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(locale, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return value;
  }
}

export function relativeTime(value: string | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString();
}

export const CATEGORY_VALUES: ActivityCategory[] = [
  'ACTIVITY_CATEGORY_ACTION',
  'ACTIVITY_CATEGORY_DECISION',
  'ACTIVITY_CATEGORY_SIGNAL',
];

export function categoryKey(value: ActivityCategory | undefined): string {
  if (!value) return 'unspecified';
  return value.replace('ACTIVITY_CATEGORY_', '').toLowerCase();
}

export const SCOPE_VALUES: ActivityScope[] = [
  'ACTIVITY_SCOPE_ENTITY',
  'ACTIVITY_SCOPE_PROJECT',
  'ACTIVITY_SCOPE_TEAM',
  'ACTIVITY_SCOPE_TENANT',
];

export function scopeKey(value: ActivityScope | undefined): string {
  if (!value) return 'unspecified';
  return value.replace('ACTIVITY_SCOPE_', '').toLowerCase();
}

export const INBOX_FILTERS = ['all', 'decisions', 'assignments', 'mentions', 'reviews', 'automations', 'updates'] as const;
export type InboxFilterKey = typeof INBOX_FILTERS[number];
